import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Loader2, Search, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  useStockMediaSearchLazyQuery,
  useTrackStockMediaUseMutation,
  StockMediaKind,
  StockMediaProvider,
} from '@/generated/graphql';
import { GET_MY_USER_MEDIA } from '@/queries/queries';
import type { StockMediaItem } from '@/lib/stock-media/types';
import { stockProviderLabel } from '@/lib/stock-media/stockItemToLayerInput';
import { userMediaToStockItem, type MyUserMediaRow } from '@/lib/stock-media/userMediaToStockItem';

// Provider availability per kind — Unsplash has no video API. 'library' (the
// user's own uploaded media) is appended last for both kinds so Pexels stays
// the default tab. Adding a stock provider: extend these maps (display labels
// come from stockProviderLabel; 'library' is special-cased via i18n instead).
const PROVIDERS_BY_KIND: Record<'video' | 'image', string[]> = {
  video: ['pexels', 'library'],
  image: ['pexels', 'unsplash', 'library'],
};
const PROVIDER_HOME: Record<string, string> = {
  pexels: 'https://www.pexels.com',
  unsplash: 'https://unsplash.com',
};

// Unsplash attribution links must carry these UTM params (their guideline);
// Pexels has no such requirement.
const withUtm = (url: string, provider: string) =>
  provider === 'unsplash'
    ? `${url}${url.includes('?') ? '&' : '?'}utm_source=autoclipper&utm_medium=referral`
    : url;

const toGqlProvider = (p: string) =>
  p === 'unsplash' ? StockMediaProvider.Unsplash : StockMediaProvider.Pexels;

export interface StockMediaBrowseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: 'video' | 'image';
  /** Picking a card fires this once and closes the dialog. */
  onPick: (item: StockMediaItem) => void;
  /** "Upload from computer" — caller closes the dialog and opens its file input. */
  onUploadInstead: () => void;
}

const ResultCard: React.FC<{ item: StockMediaItem; onClick: () => void }> = ({ item, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div className="min-w-0">
      <button
        type="button"
        data-testid={`stock-media-item-${item.id}`}
        title={item.author ?? ''}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="relative block w-full aspect-video rounded-md overflow-hidden border-2 border-border bg-black/40 hover:border-primary"
      >
        {hovered && item.kind === 'video' && item.preview_url ? (
          // Mounts on hover only, so the grid never preloads dozens of videos.
          <video
            src={item.preview_url}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover pointer-events-none"
          />
        ) : item.kind === 'video' && !item.thumbnail_url && item.file_url ? (
          // Library videos carry no server-generated thumbnail
          // (userMediaToStockItem sets thumbnail_url: '') — show the video's
          // own first frame as the still, same as MyMediaPage's MediaThumb,
          // instead of a blank tile until the user hovers.
          <video
            src={item.file_url}
            muted
            preload="metadata"
            className="w-full h-full object-cover pointer-events-none"
          />
        ) : (
          <img
            src={item.thumbnail_url}
            alt={item.author ?? ''}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        )}
      </button>
      {/* Linked attribution ("Photo by X on Unsplash" guideline) sits below
          the click surface — an <a> nested in a <button> is invalid HTML.
          Library items have no attribution or provider home link — the
          row's own name is shown instead. */}
      <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground truncate">
        {item.provider === 'library' ? (
          <span>{item.display_name}</span>
        ) : (
          <>
            {item.author &&
              (item.author_url ? (
                <a
                  href={withUtm(item.author_url, item.provider)}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline"
                >
                  {item.author}
                </a>
              ) : (
                <span>{item.author}</span>
              ))}
            {item.author ? ' · ' : null}
            <a
              href={withUtm(PROVIDER_HOME[item.provider] ?? PROVIDER_HOME.pexels, item.provider)}
              target="_blank"
              rel="noreferrer"
              className="hover:underline"
            >
              {stockProviderLabel(item.provider)}
            </a>
          </>
        )}
      </p>
    </div>
  );
};

export const StockMediaBrowseDialog: React.FC<StockMediaBrowseDialogProps> = ({
  open,
  onOpenChange,
  kind,
  onPick,
  onUploadInstead,
}) => {
  const { t } = useTranslation();
  const providers = PROVIDERS_BY_KIND[kind];
  // The dialog mounts fresh per open (Task 6 renders it conditionally), so
  // per-open state resets naturally; kind never changes while mounted.
  const [provider, setProvider] = useState(providers[0]);
  const isLibrary = provider === 'library';
  const tabLabel = (p: string) =>
    p === 'library' ? t('projectEditor.stockMedia.libraryTab') : stockProviderLabel(p);
  const providerLabel = tabLabel(provider);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [items, setItems] = useState<StockMediaItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [runSearch, { loading, error }] = useStockMediaSearchLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [trackUse] = useTrackStockMediaUseMutation();
  // Guards against out-of-order responses: switching provider/search while a
  // request is in flight must not let the stale response overwrite the grid.
  const requestSeqRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const gqlKind = kind === 'video' ? StockMediaKind.Video : StockMediaKind.Image;

  const fetchPage = useCallback(
    async (pageToLoad: number, replace: boolean) => {
      const seq = ++requestSeqRef.current;
      const { data } = await runSearch({
        variables: {
          kind: gqlKind,
          query: debouncedSearch || null,
          page: pageToLoad,
          provider: toGqlProvider(provider),
        },
      });
      // A newer request started while this one was in flight — drop it.
      if (seq !== requestSeqRef.current) return;
      const result = data?.stockMediaSearch;
      if (!result) return;
      setPage(result.page);
      setHasMore(result.has_more);
      const fresh = result.items as StockMediaItem[];
      setItems((prev) => {
        if (replace) return fresh;
        // Provider pages can overlap; dedupe by id when appending.
        const seen = new Set(prev.map((i) => i.id));
        return [...prev, ...fresh.filter((i) => !seen.has(i.id))];
      });
    },
    [runSearch, gqlKind, debouncedSearch, provider],
  );

  // Refires on query/provider change too — fetchPage's identity carries both.
  // Skips entirely for the library tab: that data comes from the Apollo
  // query below, not this stale-response-guarded fetch path, and
  // toGqlProvider('library') would otherwise default to Pexels and fire a
  // bogus stock search.
  useEffect(() => {
    if (!open || isLibrary) return;
    setItems([]);
    fetchPage(1, true);
  }, [open, fetchPage, isLibrary]);

  // Personal-library tab: plain Apollo query (cache-backed), entirely
  // separate from the stock fetchPage/requestSeqRef path above so the two
  // cannot interfere. No "load more" here — this tab shows the 500 most
  // recent items only.
  const { data: libraryData, loading: libraryLoading, error: libraryError, refetch: refetchLibrary } = useQuery(
    GET_MY_USER_MEDIA,
    { variables: { kind, limit: 500 }, skip: !isLibrary },
  );

  const libraryItems = useMemo<StockMediaItem[]>(() => {
    const rows: MyUserMediaRow[] = libraryData?.myUserMedia ?? [];
    const q = debouncedSearch.trim().toLowerCase();
    const filtered = q ? rows.filter((r) => (r.name ?? '').toLowerCase().includes(q)) : rows;
    return filtered.reduce<StockMediaItem[]>((acc, row) => {
      const item = userMediaToStockItem(row);
      if (item) acc.push(item);
      return acc;
    }, []);
  }, [libraryData, debouncedSearch]);

  const displayedItems = isLibrary ? libraryItems : items;
  const displayedLoading = isLibrary ? libraryLoading : loading;
  const displayedError = isLibrary ? Boolean(libraryError) : Boolean(error);

  const handlePick = (item: StockMediaItem) => {
    if (item.download_location) {
      // Unsplash guideline: report the use. Fire-and-forget — tracking must
      // never block or fail the insert. Library items never have a
      // download_location, so this never fires for them.
      trackUse({
        variables: {
          provider: toGqlProvider(item.provider),
          download_location: item.download_location,
        },
      }).catch(() => {});
    }
    onPick(item);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {kind === 'video'
              ? t('projectEditor.stockMedia.titleVideo')
              : t('projectEditor.stockMedia.titleImage')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('projectEditor.stockMedia.searchPlaceholder', { provider: providerLabel })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="stock-media-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('projectEditor.stockMedia.searchPlaceholder', { provider: providerLabel })}
                className="pl-8"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              data-testid="stock-media-upload-instead"
              onClick={onUploadInstead}
            >
              <Upload className="h-4 w-4 mr-1.5" />
              {t('projectEditor.stockMedia.uploadInstead')}
            </Button>
          </div>
          {providers.length > 1 && (
            <div className="flex gap-1.5">
              {providers.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`stock-media-provider-${p}`}
                  className={`text-xs px-2.5 py-1 rounded-full border whitespace-nowrap ${
                    provider === p
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-muted/30 hover:bg-muted/50'
                  }`}
                  onClick={() => setProvider(p)}
                >
                  {tabLabel(p)}
                </button>
              ))}
            </div>
          )}
          <div className="max-h-[55vh] min-h-[200px] overflow-y-auto pr-1">
            {displayedError ? (
              <div className="py-10 text-center space-y-3">
                <p className="text-sm text-muted-foreground">{t('projectEditor.stockMedia.error')}</p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  data-testid="stock-media-retry"
                  onClick={() => (isLibrary ? refetchLibrary() : fetchPage(1, true))}
                >
                  {t('projectEditor.stockMedia.retry')}
                </Button>
              </div>
            ) : displayedItems.length === 0 && displayedLoading ? (
              <div className="py-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('projectEditor.stockMedia.loading')}
              </div>
            ) : displayedItems.length === 0 ? (
              <p className="text-sm text-muted-foreground py-10 text-center">
                {t('projectEditor.stockMedia.noResults')}
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {displayedItems.map((item) => (
                    <ResultCard key={item.id} item={item} onClick={() => handlePick(item)} />
                  ))}
                </div>
                {!isLibrary && hasMore && (
                  <div className="pt-3 flex justify-center">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      data-testid="stock-media-load-more"
                      disabled={loading}
                      onClick={() => fetchPage(page + 1, false)}
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                      {t('projectEditor.stockMedia.loadMore')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
          {!isLibrary && (
            <div className="pt-1 border-t border-border">
              <a
                href={withUtm(PROVIDER_HOME[provider] ?? PROVIDER_HOME.pexels, provider)}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-muted-foreground hover:underline"
              >
                {t('projectEditor.stockMedia.providedBy', { provider: providerLabel })}
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StockMediaBrowseDialog;
